import React from 'react';
import { Sheet, SheetContent, SheetTrigger } from '../../ui/sheet';
import { Button } from '../../ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../../ui/accordion';
import { Table as UiTable, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../ui/table';
import { BookOpen } from 'lucide-react';
import { SERVICES, NRC_TABLE, hex2 } from '../../../lib/uds/constants';

const RefTable = ({ columns, rows }) => (
  <div className="rounded-md border border-structure text-xs">
    <UiTable>
      <TableHeader>
        <TableRow className="bg-muted/60 hover:bg-muted/60">
          {columns.map((c) => <TableHead key={c} className="font-mono">{c}</TableHead>)}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i} className="align-top">
            {r.map((cell, j) => <TableCell key={j} className={j === 0 ? 'font-mono whitespace-nowrap' : ''}>{cell}</TableCell>)}
          </TableRow>
        ))}
      </TableBody>
    </UiTable>
  </div>
);

const algorithmFormula = (security) => {
  const op = security.type === 'add' ? '+' : 'XOR';
  return `KEY = SEED ${op} 0x${security.value.toString(16).toUpperCase().padStart(4, '0')}`;
};

const ReferenceDrawer = ({ profile }) => (
  <Sheet>
    <SheetTrigger asChild>
      <Button variant="outline" size="sm" data-testid="uds-reference-trigger">
        <BookOpen className="h-4 w-4" /> Reference
      </Button>
    </SheetTrigger>
    <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="uds-reference-drawer">
      <div className="pt-8 space-y-4">
        <Accordion type="multiple" defaultValue={[]}>
          <AccordionItem value="explainer">
            <AccordionTrigger className="font-chivo uppercase text-sm tracking-wide">What is a UDS Session?</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed">
              A UDS (ISO 14229) diagnostic session is the ECU's current operating mode for diagnostics.
              Default Session is normal driving operation; Programming and Extended sessions unlock
              additional services (writing data, clearing faults, security access) that would be unsafe
              to allow all the time. A tester must explicitly request the session it needs before using
              those services, and the ECU enforces the rule on every request.
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="security">
            <AccordionTrigger className="font-chivo uppercase text-sm tracking-wide">Security Access Algorithm</AccordionTrigger>
            <AccordionContent className="text-sm text-muted-foreground leading-relaxed space-y-2">
              <p>This simulator's active ECU profile uses a deliberately simple, fully documented algorithm (real ECUs use proprietary or cryptographic algorithms — this one is for learning the request/response flow, not for defeating a real security system):</p>
              <p className="font-mono text-sm text-foreground bg-muted/50 rounded px-3 py-2">{algorithmFormula(profile.security)}</p>
              <p>Request a seed with 0x27 01, compute the key from it, then send the key with 0x27 02. Three wrong keys in a row triggers a timed lockout (NRC 0x36), matching real Security Access behavior.</p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="sid">
            <AccordionTrigger className="font-chivo uppercase text-sm tracking-wide">Service ID (SID) Table</AccordionTrigger>
            <AccordionContent>
              <RefTable columns={['SID', 'Name', 'Description']} rows={SERVICES.map((s) => [`0x${hex2(s.sid)}`, s.name, s.short])} />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="nrc">
            <AccordionTrigger className="font-chivo uppercase text-sm tracking-wide">Negative Response Code (NRC) Table</AccordionTrigger>
            <AccordionContent>
              <RefTable
                columns={['NRC', 'Name', 'Meaning']}
                rows={Object.entries(NRC_TABLE).map(([code, v]) => [`0x${hex2(Number(code))}`, v.name, v.meaning])}
              />
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="did">
            <AccordionTrigger className="font-chivo uppercase text-sm tracking-wide">Data Identifier (DID) Table</AccordionTrigger>
            <AccordionContent>
              <RefTable
                columns={['DID', 'Name', 'Format', 'Writable']}
                rows={profile.dids.map((d) => [`0x${hex2(d.id >> 8)}${hex2(d.id & 0xff)}`, d.name, d.computed ? 'computed' : d.format, d.writable ? 'Yes' : 'No'])}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </SheetContent>
  </Sheet>
);

export default ReferenceDrawer;
